/**
 * Deeply compares two values for equality.
 * 
 * @example
 * ```typescript
 * const a = { name: 'John', age: 30 };
 * const b = { name: 'John', age: 30 };
 * const c = { name: 'Jane', age: 25 };
 * const d = [1, 2, 3];
 * const e = [1, 2, 3];
 * const f = [1, 2, 4];
 * const g = new Date();
 * const h = new Date();
 * 
 * deepEqual(a, b) // true
 * deepEqual(a, c) // false
 * deepEqual(d, e) // true
 * deepEqual(d, f) // false
 * deepEqual(g, h) // true
 * ```
 * 
 * @param a The first value to compare.
 * @param b The second value to compare.
 * @returns True if the values are deeply equal, false otherwise.
 */
export function deepEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true;
	if (a == null || b == null) return false;
	if (typeof a !== typeof b) return false;
	if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();

	if (Array.isArray(a)) {
		return Array.isArray(b) && a.length === b.length
			&& a.every((v, i) => deepEqual(v, b[i]));
	}

	if (typeof a === 'object' && typeof b === 'object') {
		const ak = Object.keys(a as object);
		const bk = Object.keys(b as object);
		return ak.length === bk.length
			&& ak.every(k => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
	}

	return false;
}

